import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  TweaksPanel,
  TweakRadio,
  TweakSection,
  TweakToggle,
} from './XlabsTweaks';
import { useAuth } from '../Auth/AuthGate';
import { readTheme, writeTheme } from '../../utils/internalTheme';
import './InternalIndex.css';
import '../Pages/XlabsTweaks.css';

/**
 * Authenticated backend areas index.
 * Blueprint: docs/INTERNAL_INDEX.md · docs/NATIVE_AUTH.md
 */

/**
 * @see docs/INTERNAL_INDEX.md — Areas (live only).
 * Media / Proposals / PJM are parked — see § Parked areas. Do not re-add here
 * without flipping ENABLE_INTERNAL_PARKED_ROUTES in src/App.js.
 */
const AREAS = [
  {
    num: '01',
    key: 'xlabs',
    href: '/xlabs-hub',
    name: 'xlabs',
    sub: 'Configurator hub · Locate · Design',
    meta: ['configurator'],
  },
  {
    num: '02',
    key: 'sim-lab',
    href: '/internal/sim-lab',
    name: 'Sim lab',
    sub: 'Model load · localize · screenshots',
    meta: ['viewer', 'research'],
  },
  {
    num: '03',
    key: 'users',
    href: '/internal/users',
    name: 'Users',
    sub: 'Invites · roles · allowlist',
    meta: ['admin'],
    adminOnly: true,
  },
];

function AreaLink({ href, className, children }) {
  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

export default function InternalIndex() {
  const { email, role, isAdmin, logout } = useAuth();
  const [theme, setTheme] = useState(readTheme);
  const [showMeta, setShowMeta] = useState(false);

  useEffect(() => {
    writeTheme(theme);
  }, [theme]);

  const onLogout = useCallback(() => {
    logout();
  }, [logout]);

  const visibleAreas = useMemo(
    () => AREAS.filter((a) => !a.adminOnly || isAdmin),
    [isAdmin]
  );

  return (
    <div
      className="internal-index"
      data-hub-theme={theme === 'light' ? undefined : theme}
    >
      <Helmet>
        <title>internal · construx</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="hub">
        <div className="hub-card">
          <div className="hub-h">construx · internal</div>
          <h1 className="hub-title">Backend areas</h1>
          <p className="hub-sub">
            xlabs, sim lab, and team tools. Signed-in team access only.
          </p>

          <div className="nav-grid">
            {visibleAreas.map((a) => (
              <AreaLink key={a.key} className="card" href={a.href}>
                <span className="num">{a.num}</span>
                <span className="name">{a.name}</span>
                <span className="sub">{a.sub}</span>
                {showMeta && (
                  <span className="meta">
                    {a.meta.map((m) => (
                      <span key={m}>{m}</span>
                    ))}
                  </span>
                )}
              </AreaLink>
            ))}
          </div>

          <div className="hub-foot">
            <div className="hub-foot-status">
              <span className="pip" aria-hidden="true" />
              <span>session</span>
              {email ? <span className="email">{email}</span> : null}
              {role ? <span className="access-panel__muted">· {role}</span> : null}
            </div>
            <div className="hub-foot-links">
              <button type="button" className="logout" onClick={onLogout}>
                logout
              </button>
            </div>
          </div>
        </div>

        <TweaksPanel title="View" defaultOpen={false}>
          <TweakSection label="Theme" />
          <TweakRadio
            label="Skin"
            value={theme}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'paper', label: 'Paper' },
              { value: 'blueprint', label: 'Blue' },
              { value: 'terminal', label: 'Term' },
            ]}
            onChange={setTheme}
          />
          <TweakToggle
            label="Show meta tags"
            value={showMeta}
            onChange={setShowMeta}
          />
        </TweaksPanel>
      </div>
    </div>
  );
}
